import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ClipboardCheck, FileText, Zap, Fuel, CheckCircle, AlertTriangle,
  Eye, Building2, ExternalLink, X, ChevronDown, ChevronRight, XCircle,
  FileSearch, ArrowLeft, Save, SplitSquareHorizontal, Filter, Clock,
  Sparkles, Loader2, RefreshCw, Scissors, Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { verificationAPI } from '../utils/api';
import { prospectsAPI } from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PreviewButton, getPreviewUrl } from '../components/FilePreviewModal';

const STATUS_STYLES = {
  PENDING: 'bg-slate-100 text-slate-700 border-slate-200',
  NEEDS_REVIEW: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
};

const FILTER_TABS = [
  { key: null, label: 'Pending', icon: Clock },
  { key: 'NEEDS_REVIEW', label: 'Needs Review', icon: AlertTriangle },
  { key: 'APPROVED', label: 'Approved', icon: CheckCircle },
  { key: 'REJECTED', label: 'Rejected', icon: XCircle },
  { key: 'ALL', label: 'All Files', icon: Filter },
];

export default function VerificationQueuePage() {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [reviewFile, setReviewFile] = useState(null);
  const [reviewDetail, setReviewDetail] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedSites, setExpandedSites] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  const fetchQueue = useCallback(async () => {
    try {
      const res = await verificationAPI.getQueue(activeFilter);
      setQueue(res.data);
      const siteKeys = {};
      for (const f of (res.data?.pending_files || [])) {
        siteKeys[f.entity_id || 'unknown'] = true;
      }
      setExpandedSites(siteKeys);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, [activeFilter]);

  const openReview = useCallback(async (uploadId) => {
    setReviewLoading(true);
    setReviewFile(uploadId);
    try {
      const res = await verificationAPI.getFileDetail(uploadId);
      setReviewDetail(res.data);
    } catch { toast.error('Failed to load file details'); setReviewFile(null); }
    finally { setReviewLoading(false); }
  }, []);

  useEffect(() => {
    fetchQueue();
    // Deep-link: auto-open file if upload_id is in URL
    const fileId = searchParams.get('file');
    if (fileId) openReview(fileId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const closeReview = () => { setReviewFile(null); setReviewDetail(null); };

  const handleProcess = async (uploadId) => {
    try {
      await verificationAPI.processFile(uploadId);
      toast.success('File marked for review');
      fetchQueue();
    } catch { toast.error('Failed to process'); }
  };

  const handleApproveFile = async (uploadId) => {
    setSubmitting(true);
    try {
      await verificationAPI.approveFile(uploadId);
      toast.success('File approved');
      closeReview();
      fetchQueue();
    } catch { toast.error('Failed to approve'); }
    finally { setSubmitting(false); }
  };

  const openRejectDialog = (file) => {
    setRejectTarget(file);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleRejectFile = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setSubmitting(true);
    try {
      await verificationAPI.rejectFile(rejectTarget.upload_id || rejectTarget, rejectReason);
      toast.success('File rejected');
      setShowRejectDialog(false);
      closeReview();
      fetchQueue();
    } catch { toast.error('Failed to reject'); }
    finally { setSubmitting(false); }
  };

  const groupedFiles = useMemo(() => {
    const files = queue?.pending_files || [];
    const groups = {};
    for (const f of files) {
      const key = f.entity_id || 'unknown';
      if (!groups[key]) {
        groups[key] = { site_id: key, site_name: f.site_name || 'Unknown Site', company_name: f.company_name || '', files: [] };
      }
      groups[key].files.push(f);
    }
    return Object.values(groups);
  }, [queue]);

  const toggleSite = (siteId) => setExpandedSites(prev => ({ ...prev, [siteId]: !prev[siteId] }));

  const counts = queue?.counts || {};

  if (loading) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;

  return (
    <div className="flex min-h-screen bg-slate-50/50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 md:p-8 max-w-[1920px] mx-auto space-y-6">
          <PageHeader
            title="Verification Queue"
            description="Review uploaded documents, enter extracted data, and approve or reject"
          />

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="queue-summary">
            <SummaryCard icon={Clock} title="Pending" value={counts.PENDING || 0} color="amber" />
            <SummaryCard icon={AlertTriangle} title="Needs Review" value={counts.NEEDS_REVIEW || 0} color="blue" />
            <SummaryCard icon={CheckCircle} title="Approved" value={counts.APPROVED || 0} color="green" />
            <SummaryCard icon={XCircle} title="Rejected" value={counts.REJECTED || 0} color="red" />
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-sm p-1" data-testid="filter-tabs">
            {FILTER_TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeFilter === tab.key;
              return (
                <button
                  key={tab.key || 'pending'}
                  onClick={() => { setActiveFilter(tab.key); setLoading(true); }}
                  data-testid={`filter-${tab.key || 'pending'}`}
                  className={`flex items-center space-x-1.5 px-4 py-2 rounded-sm text-xs font-medium transition-all
                    ${isActive ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                  {tab.key === null && counts.PENDING > 0 && !isActive && (
                    <span className="ml-1 bg-amber-100 text-amber-700 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded">{counts.PENDING + (counts.NEEDS_REVIEW || 0)}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Review Panel or Queue List */}
          {reviewFile ? (
            <ReviewPanel
              detail={reviewDetail}
              loading={reviewLoading}
              onClose={closeReview}
              onApprove={handleApproveFile}
              onReject={(file) => openRejectDialog(file)}
              onProcess={handleProcess}
              submitting={submitting}
              navigate={navigate}
              onRefresh={() => openReview(reviewFile)}
              token={token}
            />
          ) : (
            <FileQueueList
              groupedFiles={groupedFiles}
              expandedSites={expandedSites}
              toggleSite={toggleSite}
              onReview={openReview}
              onProcess={handleProcess}
              onApprove={handleApproveFile}
              onReject={openRejectDialog}
              navigate={navigate}
              token={token}
            />
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Reject File</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rejection Reason *</Label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Describe why this file is being rejected..."
                className="mt-1"
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectFile} disabled={submitting} data-testid="confirm-reject-btn">
                {submitting ? 'Rejecting...' : 'Reject File'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ==================== FILE QUEUE LIST ==================== */
function FileQueueList({ groupedFiles, expandedSites, toggleSite, onReview, onProcess, onApprove, onReject, navigate, token }) {
  if (groupedFiles.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="empty-queue">
        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-200" strokeWidth={1} />
        <h3 className="font-heading text-base font-semibold text-slate-700 mb-1">Queue Clear</h3>
        <p className="text-sm text-slate-400">No files matching the current filter</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="files-queue">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-heading text-sm font-semibold tracking-tight text-slate-900 flex items-center">
          <ClipboardCheck className="w-4 h-4 mr-2 text-amber-600" />
          Uploaded Files
        </h3>
        <span className="font-mono text-xs text-slate-400">{groupedFiles.reduce((n, g) => n + g.files.length, 0)} files</span>
      </div>
      <div className="divide-y divide-slate-100">
        {groupedFiles.map(group => (
          <div key={group.site_id} data-testid={`site-group-${group.site_id}`}>
            <div className="w-full px-6 py-3 flex items-center justify-between bg-slate-50/50 hover:bg-slate-100/50 transition-colors">
              <button
                className="flex items-center space-x-3 flex-1"
                onClick={() => toggleSite(group.site_id)}
              >
                {expandedSites[group.site_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <Building2 className="w-4 h-4 text-slate-500" />
                <div className="text-left">
                  <span className="text-sm font-medium text-slate-900">{group.site_name}</span>
                  {group.company_name && <span className="text-xs text-slate-400 ml-2">{group.company_name}</span>}
                </div>
                <span className="font-mono text-xs text-slate-400 ml-2">({group.files.length})</span>
              </button>
              <button
                className="text-xs text-blue-600 hover:underline flex items-center"
                onClick={() => navigate(`/prospects/sites/${group.site_id}?stage=requirement`)}
                data-testid={`deep-link-${group.site_id}`}
              >
                <ExternalLink className="w-3 h-3 mr-1" /> View Site
              </button>
            </div>

            {expandedSites[group.site_id] && (
              <div className="divide-y divide-slate-50">
                {group.files.map(f => (
                  <FileRow
                    key={f.upload_id}
                    file={f}
                    onReview={() => onReview(f.upload_id)}
                    onProcess={() => onProcess(f.upload_id)}
                    onApprove={() => onApprove(f.upload_id)}
                    onReject={() => onReject(f)}
                    token={token}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FileRow({ file: f, onReview, onProcess, onApprove, onReject, token }) {
  return (
    <div className="px-6 py-3 pl-16 hover:bg-slate-50/50 transition-colors group" data-testid={`queue-file-${f.upload_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <FileText className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</p>
            <p className="text-xs text-slate-500">
              <span className="capitalize">{f.file_type?.replace(/_/g, ' ')}</span>
              <span className="mx-1.5 text-slate-300">·</span>
              <span className="font-mono text-[11px]">{new Date(f.uploaded_at).toLocaleDateString()}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm border ${STATUS_STYLES[f.parsing_status] || STATUS_STYLES.PENDING}`}>
            {f.parsing_status}
          </span>
          {f.extraction_status === 'success' && (
            <span className="text-[10px] font-mono text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm flex items-center" data-testid={`ai-badge-${f.upload_id}`}>
              <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI
            </span>
          )}
          {f.extraction_status === 'PROCESSING' && (
            <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm flex items-center">
              <Loader2 className="w-2.5 h-2.5 mr-0.5 animate-spin" />
            </span>
          )}
          {f.storage_path && (
            <PreviewButton uploadId={f.upload_id} filename={f.original_filename} token={token} size="xs" />
          )}
          {/* Primary action: Open Document Reviewer */}
          <Button size="sm" variant="outline" className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={onReview} data-testid={`review-file-${f.upload_id}`}>
            <FileSearch className="w-3 h-3 mr-1" /> Review
          </Button>
          {f.parsing_status === 'PENDING' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onProcess}>
              <AlertTriangle className="w-3 h-3 mr-1" /> Mark
            </Button>
          )}
          {f.parsing_status !== 'APPROVED' && (
            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={onReject} data-testid={`reject-file-${f.upload_id}`}>
              <XCircle className="w-3 h-3" />
            </Button>
          )}
          {f.parsing_status !== 'APPROVED' && (
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onApprove} data-testid={`approve-file-${f.upload_id}`}>
              <CheckCircle className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      {f.rejection_reason && (
        <p className="text-xs text-red-500 mt-1 pl-7">Rejected: {f.rejection_reason}</p>
      )}
    </div>
  );
}


/* ==================== REVIEW PANEL (Split View) ==================== */
function ReviewPanel({ detail, loading, onClose, onApprove, onReject, onProcess, submitting, navigate, onRefresh, token }) {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (detail) {
      // Pre-populate form from extracted_data or linked_data
      const existing = detail.extracted_data || {};
      const linked = detail.linked_data || {};
      setFormData({
        // Bill fields
        bill_month: existing.bill_month || linked.bill_month || '',
        billing_start: existing.billing_start || linked.billing_start || '',
        billing_end: existing.billing_end || linked.billing_end || '',
        total_kwh: existing.total_kwh || linked.total_kwh || '',
        max_demand_kw: existing.max_demand_kw || linked.max_demand_kw || '',
        sanctioned_load_kw: existing.sanctioned_load_kw || linked.sanctioned_load_kw || '',
        tariff_category: existing.tariff_category || linked.tariff_category || '',
        // DG Log fields
        month: existing.month || linked.month || '',
        runtime_hours: existing.runtime_hours || linked.runtime_hours || '',
        diesel_liters: existing.diesel_liters || linked.diesel_liters || '',
        kwh_estimated: existing.kwh_estimated || linked.kwh_estimated || '',
        notes: existing.notes || linked.notes || '',
      });
    }
  }, [detail]);

  const handleFieldChange = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const handleSaveCorrections = async () => {
    setSaving(true);
    try {
      await verificationAPI.correctFile(detail.upload_id, formData);
      toast.success('Corrections saved');
    } catch { toast.error('Failed to save corrections'); }
    finally { setSaving(false); }
  };

  const handleSaveAndApprove = async () => {
    setSaving(true);
    try {
      // Save corrections first
      await verificationAPI.correctFile(detail.upload_id, formData);

      // If it's a bill, also create/update the bill record
      if (detail.file_type === 'electricity_bill' && formData.total_kwh) {
        await prospectsAPI.createBill({
          upload_id: detail.upload_id,
          prospect_site_id: detail.entity_id,
          bill_month: formData.bill_month,
          billing_start: formData.billing_start,
          billing_end: formData.billing_end,
          total_kwh: formData.total_kwh ? Number(formData.total_kwh) : null,
          max_demand_kw: formData.max_demand_kw ? Number(formData.max_demand_kw) : null,
          sanctioned_load_kw: formData.sanctioned_load_kw ? Number(formData.sanctioned_load_kw) : null,
          tariff_category: formData.tariff_category,
        });
      }

      // If it's a DG log, also create/update the log record
      if (detail.file_type === 'dg_log' && formData.runtime_hours) {
        await prospectsAPI.createDGLog({
          upload_id: detail.upload_id,
          prospect_site_id: detail.entity_id,
          dg_asset_id: '',
          month: formData.month,
          runtime_hours: formData.runtime_hours ? Number(formData.runtime_hours) : null,
          diesel_liters: formData.diesel_liters ? Number(formData.diesel_liters) : null,
          kwh_estimated: formData.kwh_estimated ? Number(formData.kwh_estimated) : null,
          notes: formData.notes,
        });
      }

      // Approve the file
      await onApprove(detail.upload_id);
      toast.success('Data saved and file approved');
    } catch { toast.error('Failed to save and approve'); }
    finally { setSaving(false); }
  };

  const handleAutoExtract = async () => {
    setExtracting(true);
    try {
      const res = await verificationAPI.extractFile(detail.upload_id);
      if (res.data.success && res.data.fields) {
        // Merge extracted fields into form
        const fields = res.data.fields;
        setFormData(prev => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(fields)) {
            if (v !== null && v !== undefined && v !== '') {
              merged[k] = v;
            }
          }
          return merged;
        });
        toast.success(`AI extracted ${Object.keys(fields).filter(k => fields[k] !== null).length} fields (${Math.round((res.data.confidence || 0) * 100)}% confidence)`);
      } else {
        toast.error('Extraction failed — file may not be available in storage');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'AI extraction failed';
      toast.error(msg);
    }
    finally { setExtracting(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (!detail) return null;

  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(detail.original_filename || '');
  const isPdf = /\.pdf$/i.test(detail.original_filename || '');
  const hasStoragePath = !!detail.storage_path;
  const previewUrl = hasStoragePath ? `${getPreviewUrl(detail.upload_id)}?token=${encodeURIComponent(token)}` : null;
  const isBill = detail.file_type === 'electricity_bill';
  const isDGLog = detail.file_type === 'dg_log';

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="review-panel">
      {/* Review Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center space-x-3">
          <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-sm transition-colors" data-testid="close-review-btn">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div>
            <h3 className="font-heading text-sm font-semibold tracking-tight text-slate-900 flex items-center">
              <SplitSquareHorizontal className="w-4 h-4 mr-2 text-blue-600" />
              Document Review
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {detail.original_filename}
              <span className="mx-1.5 text-slate-300">·</span>
              <span className="capitalize">{detail.file_type?.replace(/_/g, ' ')}</span>
              <span className="mx-1.5 text-slate-300">·</span>
              <span className={`font-semibold ${STATUS_STYLES[detail.parsing_status]?.split(' ')[1] || 'text-slate-600'}`}>{detail.parsing_status}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {detail.site_name && (
            <button
              className="text-xs text-blue-600 hover:underline flex items-center"
              onClick={() => navigate(`/prospects/sites/${detail.entity_id}?stage=requirement`)}
              data-testid="review-deep-link"
            >
              <Building2 className="w-3 h-3 mr-1" /> {detail.site_name}
            </button>
          )}
          <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-sm border ${STATUS_STYLES[detail.parsing_status] || STATUS_STYLES.PENDING}`}>
            {detail.parsing_status}
          </span>
        </div>
      </div>

      {/* Split Panel: Document Viewer + Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200 min-h-[500px]">
        {/* LEFT: Document Viewer */}
        <div className="flex flex-col" data-testid="document-viewer">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
            <p className="section-label">Document Preview</p>
          </div>
          <div className="flex-1 flex items-center justify-center bg-slate-100/50 p-4">
            {previewUrl && isPdf ? (
              <iframe
                src={previewUrl}
                className="w-full h-full min-h-[460px] rounded border border-slate-200 bg-white"
                title="Document preview"
                data-testid="pdf-viewer"
              />
            ) : previewUrl && isImage ? (
              <img
                src={previewUrl}
                alt={detail.original_filename}
                className="max-w-full max-h-[460px] object-contain rounded border border-slate-200 bg-white"
                data-testid="image-viewer"
              />
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-200" strokeWidth={1} />
                <p className="text-sm font-medium text-slate-500 mb-1">{detail.original_filename}</p>
                <p className="text-xs text-slate-400 mb-4 max-w-xs mx-auto">
                  {hasStoragePath
                    ? 'Loading preview...'
                    : 'This file was uploaded before cloud storage was enabled. Re-upload the document to view it here.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Editable Fields */}
        <div className="flex flex-col" data-testid="extracted-fields">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <p className="section-label !mb-0">Extracted / Editable Fields</p>
            <div className="flex items-center space-x-2">
              {detail.extraction_confidence > 0 && (
                <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm" data-testid="extraction-confidence">
                  AI {Math.round(detail.extraction_confidence * 100)}%
                </span>
              )}
              {detail.extraction_status === 'PROCESSING' && (
                <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-sm flex items-center">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Extracting...
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
                onClick={handleAutoExtract}
                disabled={extracting}
                data-testid="auto-extract-btn"
              >
                {extracting ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Extracting...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> AI Extract</>
                )}
              </Button>
              {isPdf && isBill && !detail.is_split_parent && (
                <SplitBillButton uploadId={detail.upload_id} onSplit={onRefresh} />
              )}
            </div>
          </div>
          <div className="flex-1 p-5 overflow-y-auto">
            {isBill ? (
              <BillFields data={formData} onChange={handleFieldChange} />
            ) : isDGLog ? (
              <DGLogFields data={formData} onChange={handleFieldChange} />
            ) : (
              <GenericFields data={formData} onChange={handleFieldChange} fileType={detail.file_type} />
            )}

            {detail.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm">
                <p className="text-xs font-semibold text-red-700 mb-1">Previous Rejection Reason:</p>
                <p className="text-xs text-red-600">{detail.rejection_reason}</p>
              </div>
            )}
          </div>

          {/* Actions Footer */}
          <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={handleSaveCorrections} disabled={saving} data-testid="save-corrections-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Corrections'}
            </Button>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onReject(detail.upload_id)}
                disabled={submitting || detail.parsing_status === 'APPROVED'}
                data-testid="review-reject-btn"
              >
                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleSaveAndApprove}
                disabled={submitting || saving || detail.parsing_status === 'APPROVED'}
                data-testid="review-approve-btn"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> {(submitting || saving) ? 'Processing...' : 'Save & Approve'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ==================== FIELD FORMS ==================== */
function BillFields({ data, onChange }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-slate-500 flex items-center"><Zap className="w-3.5 h-3.5 mr-1.5 text-blue-500" /> Electricity Bill Fields</p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Bill Month" value={data.bill_month} onChange={v => onChange('bill_month', v)} placeholder="2026-03" testId="field-bill-month" />
        <Field label="Tariff Category" value={data.tariff_category} onChange={v => onChange('tariff_category', v)} placeholder="LT/HT" testId="field-tariff" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Billing Start" value={data.billing_start} onChange={v => onChange('billing_start', v)} type="date" testId="field-billing-start" />
        <Field label="Billing End" value={data.billing_end} onChange={v => onChange('billing_end', v)} type="date" testId="field-billing-end" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Total kWh" value={data.total_kwh} onChange={v => onChange('total_kwh', v)} type="number" testId="field-total-kwh" required />
        <Field label="Max Demand (kW)" value={data.max_demand_kw} onChange={v => onChange('max_demand_kw', v)} type="number" testId="field-max-demand" />
        <Field label="Sanctioned Load (kW)" value={data.sanctioned_load_kw} onChange={v => onChange('sanctioned_load_kw', v)} type="number" testId="field-sanctioned-load" />
      </div>
    </div>
  );
}

function DGLogFields({ data, onChange }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-slate-500 flex items-center"><Fuel className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> DG Log Fields</p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Month" value={data.month} onChange={v => onChange('month', v)} placeholder="2026-03" testId="field-dg-month" />
        <Field label="Runtime Hours" value={data.runtime_hours} onChange={v => onChange('runtime_hours', v)} type="number" testId="field-runtime-hours" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Diesel Liters" value={data.diesel_liters} onChange={v => onChange('diesel_liters', v)} type="number" testId="field-diesel-liters" />
        <Field label="Estimated kWh" value={data.kwh_estimated} onChange={v => onChange('kwh_estimated', v)} type="number" testId="field-kwh-estimated" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Notes</Label>
        <Textarea value={data.notes || ''} onChange={e => onChange('notes', e.target.value)} className="mt-1 text-sm" rows={3} data-testid="field-dg-notes" />
      </div>
    </div>
  );
}

function GenericFields({ data, onChange, fileType }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-slate-500 flex items-center"><FileText className="w-3.5 h-3.5 mr-1.5 text-slate-400" /> Document Fields ({fileType?.replace(/_/g, ' ')})</p>
      <div>
        <Label className="text-xs text-slate-500">Notes / Extracted Data</Label>
        <Textarea value={data.notes || ''} onChange={e => onChange('notes', e.target.value)} className="mt-1 text-sm" rows={6} placeholder="Add any notes or extracted data from this document..." data-testid="field-generic-notes" />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, testId, required }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 text-sm ${type === 'number' ? 'font-mono' : ''}`}
        data-testid={testId}
        required={required}
      />
    </div>
  );
}


/* ==================== SPLIT BILL BUTTON + MODAL ==================== */
function SplitBillButton({ uploadId, onSplit }) {
  const [open, setOpen] = useState(false);
  const [splits, setSplits] = useState([{ _key: `split-${Date.now()}`, page_start: 1, page_end: 1, bill_month: '' }]);
  const [saving, setSaving] = useState(false);

  const addSplit = () => setSplits(prev => [...prev, { _key: `split-${Date.now()}-${prev.length}`, page_start: 1, page_end: 1, bill_month: '' }]);
  const removeSplit = (i) => setSplits(prev => prev.filter((_, idx) => idx !== i));
  const updateSplit = (i, key, val) => setSplits(prev => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

  const handleSubmit = async () => {
    const valid = splits.every(s => s.page_start > 0 && s.page_end >= s.page_start && s.bill_month);
    if (!valid) { toast.error('Each split needs valid page range and bill month'); return; }
    setSaving(true);
    try {
      await verificationAPI.splitBills(uploadId, splits.map(({ _key, ...s }) => s));
      toast.success(`Split into ${splits.length} bills`);
      setOpen(false);
      onSplit?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Split failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
        onClick={() => setOpen(true)} data-testid="split-bill-btn">
        <Scissors className="w-3 h-3 mr-1" /> Split Bills
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="split-bill-modal">
          <DialogHeader><DialogTitle>Split into Multiple Bills</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-3">Define page ranges and assign a bill month for each split.</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {splits.map((s, i) => (
              <div key={s._key || i} className="flex items-center gap-2 bg-slate-50 rounded p-2" data-testid={`split-row-${i}`}>
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px]">Start Page</Label>
                    <Input type="number" min={1} value={s.page_start} onChange={e => updateSplit(i, 'page_start', parseInt(e.target.value) || 1)} className="h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">End Page</Label>
                    <Input type="number" min={1} value={s.page_end} onChange={e => updateSplit(i, 'page_end', parseInt(e.target.value) || 1)} className="h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Bill Month</Label>
                    <Input type="month" value={s.bill_month} onChange={e => updateSplit(i, 'bill_month', e.target.value)} className="h-7 text-xs" />
                  </div>
                </div>
                {splits.length > 1 && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => removeSplit(i)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2">
            <Button size="sm" variant="outline" onClick={addSplit} className="text-xs"><Plus className="w-3 h-3 mr-1" /> Add Split</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="split-confirm-btn">
                {saving ? 'Splitting...' : `Split into ${splits.length} bills`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ==================== SUMMARY CARD ==================== */
const SummaryCard = ({ icon: Icon, title, value, color = 'slate' }) => {
  const colors = {
    amber: 'border-l-amber-400',
    blue: 'border-l-blue-400',
    green: 'border-l-emerald-400',
    red: 'border-l-red-400',
    slate: 'border-l-slate-400',
  };
  return (
    <div className={`bg-white border border-slate-200 border-l-[3px] ${colors[color]} rounded-sm p-4`} data-testid={`summary-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center space-x-2 mb-1">
        <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
        <p className="section-label !mb-0">{title}</p>
      </div>
      <p className="font-mono text-2xl font-medium text-slate-900">{value}</p>
    </div>
  );
};
